import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useRef, useState, type FormEvent } from 'react';
import { useOutletContext } from 'react-router';
import { api, unwrap } from '../api/client';
import { usePositions, useServices } from '../api/hooks';
import { ErrorNote, Loading, PageHead, formatDate } from '../components/ui';

interface MeContext {
  roles: Array<{ role: string }>;
}

interface ServiceNode {
  id: string;
  code: string;
  name: string;
  children: ServiceNode[];
}

export function ServicesPage() {
  const me = useOutletContext<MeContext>();
  const canWrite = me.roles.some((r) => r.role === 'drh' || r.role === 'admin');
  const services = useServices();
  const qc = useQueryClient();
  const dialog = useRef<HTMLDialogElement>(null);
  const [form, setForm] = useState({ code: '', name: '', parentId: '' });
  const create = useMutation({
    mutationFn: async () => unwrap(await api.POST('/api/v1/services', { body: { code: form.code, name: form.name, parentId: form.parentId || null } })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['services'] });
      dialog.current?.close();
      setForm({ code: '', name: '', parentId: '' });
    },
  });

  const tree = useMemo(() => {
    const nodes = new Map<string, ServiceNode>();
    for (const s of services.data ?? []) nodes.set(s.id, { id: s.id, code: s.code, name: s.name, children: [] });
    const roots: ServiceNode[] = [];
    for (const s of services.data ?? []) {
      const node = nodes.get(s.id)!;
      const parent = s.parentId ? nodes.get(s.parentId) : undefined;
      if (parent) parent.children.push(node);
      else roots.push(node);
    }
    return roots;
  }, [services.data]);

  const render = (list: ServiceNode[]) => (
    <ul>
      {list.map((n) => (
        <li key={n.id}>
          <span className="code">{n.code}</span>
          {n.name}
          {n.children.length > 0 && render(n.children)}
        </li>
      ))}
    </ul>
  );

  return (
    <>
      <PageHead
        eyebrow="Organisation"
        title="Organigramme"
        actions={
          canWrite && (
            <button className="btn btn-primary" onClick={() => dialog.current?.showModal()}>
              Nouveau service
            </button>
          )
        }
      >
        Les services de la collectivité et leur hiérarchie. Le périmètre d'un chef de service suit cet arbre.
      </PageHead>
      <ErrorNote error={services.error} />
      {services.isPending ? <Loading /> : <div className="card tree">{render(tree)}</div>}

      <dialog ref={dialog} aria-labelledby="svc-title">
        <form
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          <h2 id="svc-title">Nouveau service</h2>
          <div className="form-grid">
            <div className="field">
              <label htmlFor="svc-code">Code</label>
              <input id="svc-code" required maxLength={32} value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))} placeholder="DST-BAT" />
            </div>
            <div className="field">
              <label htmlFor="svc-name">Nom</label>
              <input id="svc-name" required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
            </div>
          </div>
          <div className="field">
            <label htmlFor="svc-parent">Rattaché à</label>
            <select id="svc-parent" value={form.parentId} onChange={(e) => setForm((f) => ({ ...f, parentId: e.target.value }))}>
              <option value="">— Racine —</option>
              {(services.data ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.code} · {s.name}
                </option>
              ))}
            </select>
          </div>
          <ErrorNote error={create.error} />
          <div className="actions">
            <button type="submit" className="btn btn-primary" disabled={create.isPending}>
              Créer
            </button>
            <button type="button" className="btn" onClick={() => dialog.current?.close()}>
              Annuler
            </button>
          </div>
        </form>
      </dialog>
    </>
  );
}

export function PositionsPage() {
  const me = useOutletContext<MeContext>();
  const canWrite = me.roles.some((r) => r.role === 'drh' || r.role === 'admin');
  const positions = usePositions();
  const services = useServices();
  const deliberations = useQuery({ queryKey: ['deliberations'], queryFn: async () => unwrap(await api.GET('/api/v1/deliberations')) });
  const qc = useQueryClient();

  const posDialog = useRef<HTMLDialogElement>(null);
  const delDialog = useRef<HTMLDialogElement>(null);
  const [pos, setPos] = useState({ serviceId: '', deliberationId: '', code: '', title: '', category: '' });
  const [del, setDel] = useState({ number: '', decidedOn: '', subject: '' });

  const createPosition = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.POST('/api/v1/positions', {
          body: { serviceId: pos.serviceId, deliberationId: pos.deliberationId || null, code: pos.code, title: pos.title, category: pos.category || null },
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['positions'] });
      posDialog.current?.close();
      setPos({ serviceId: '', deliberationId: '', code: '', title: '', category: '' });
    },
  });
  const createDeliberation = useMutation({
    mutationFn: async () => unwrap(await api.POST('/api/v1/deliberations', { body: del })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['deliberations'] });
      delDialog.current?.close();
      setDel({ number: '', decidedOn: '', subject: '' });
    },
  });

  return (
    <>
      <PageHead
        eyebrow="Organisation"
        title="Postes"
        actions={
          canWrite && (
            <>
              <button className="btn" onClick={() => delDialog.current?.showModal()}>
                Nouvelle délibération
              </button>
              <button className="btn btn-primary" onClick={() => posDialog.current?.showModal()}>
                Nouveau poste
              </button>
            </>
          )
        }
      >
        Emplois budgétaires ouverts, avec leur occupation à ce jour. Un poste est créé par une délibération du conseil.
      </PageHead>
      <ErrorNote error={positions.error} />
      {positions.isPending ? (
        <Loading />
      ) : (
        <div className="tw">
          <table>
            <thead>
              <tr>
                <th>Code</th>
                <th>Intitulé</th>
                <th>Service</th>
                <th>Catégorie</th>
                <th>Délibération</th>
                <th>Occupation</th>
              </tr>
            </thead>
            <tbody>
              {(positions.data ?? []).map((p) => (
                <tr key={p.position.id}>
                  <td className="mono">{p.position.code}</td>
                  <td>{p.position.title}</td>
                  <td>
                    {p.service.name} <span className="mono muted">{p.service.code}</span>
                  </td>
                  <td>{p.position.category ?? '—'}</td>
                  <td className="small muted">{deliberations.data?.find((d) => d.id === p.position.deliberationId)?.number ?? '—'}</td>
                  <td>{p.occupied ? <span className="b b-ok">Occupé</span> : <span className="b b-warn">Vacant</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(deliberations.data ?? []).length > 0 && (
        <section style={{ marginTop: 24 }}>
          <h2>Délibérations</h2>
          <div className="tw" style={{ marginTop: 10 }}>
            <table>
              <thead>
                <tr>
                  <th>Numéro</th>
                  <th>Date</th>
                  <th>Objet</th>
                </tr>
              </thead>
              <tbody>
                {(deliberations.data ?? []).map((d) => (
                  <tr key={d.id}>
                    <td className="mono">{d.number}</td>
                    <td>{formatDate(d.decidedOn)}</td>
                    <td>{d.subject}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <dialog ref={posDialog} aria-labelledby="pos-title">
        <form
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            createPosition.mutate();
          }}
        >
          <h2 id="pos-title">Nouveau poste</h2>
          <div className="field">
            <label htmlFor="p-service">Service</label>
            <select id="p-service" required value={pos.serviceId} onChange={(e) => setPos((f) => ({ ...f, serviceId: e.target.value }))}>
              <option value="">— Choisir —</option>
              {(services.data ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.code} · {s.name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-grid">
            <div className="field">
              <label htmlFor="p-code">Code</label>
              <input id="p-code" required maxLength={32} value={pos.code} onChange={(e) => setPos((f) => ({ ...f, code: e.target.value.toUpperCase() }))} placeholder="DST-02" />
            </div>
            <div className="field">
              <label htmlFor="p-cat">Catégorie</label>
              <input id="p-cat" maxLength={8} value={pos.category} onChange={(e) => setPos((f) => ({ ...f, category: e.target.value.toUpperCase() }))} placeholder="A, B, C…" />
            </div>
          </div>
          <div className="field">
            <label htmlFor="p-title">Intitulé</label>
            <input id="p-title" required value={pos.title} onChange={(e) => setPos((f) => ({ ...f, title: e.target.value }))} />
          </div>
          <div className="field">
            <label htmlFor="p-delib">Délibération créant le poste</label>
            <select id="p-delib" value={pos.deliberationId} onChange={(e) => setPos((f) => ({ ...f, deliberationId: e.target.value }))}>
              <option value="">— Aucune / à rattacher plus tard —</option>
              {(deliberations.data ?? []).map((d) => (
                <option key={d.id} value={d.id}>
                  {d.number} · {d.subject}
                </option>
              ))}
            </select>
          </div>
          <ErrorNote error={createPosition.error} />
          <div className="actions">
            <button type="submit" className="btn btn-primary" disabled={createPosition.isPending}>
              Créer le poste
            </button>
            <button type="button" className="btn" onClick={() => posDialog.current?.close()}>
              Annuler
            </button>
          </div>
        </form>
      </dialog>

      <dialog ref={delDialog} aria-labelledby="del-title">
        <form
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            createDeliberation.mutate();
          }}
        >
          <h2 id="del-title">Nouvelle délibération</h2>
          <div className="form-grid">
            <div className="field">
              <label htmlFor="d-number">Numéro</label>
              <input id="d-number" required value={del.number} onChange={(e) => setDel((f) => ({ ...f, number: e.target.value }))} placeholder="DEL-2026-045" />
            </div>
            <div className="field">
              <label htmlFor="d-date">Date du conseil</label>
              <input id="d-date" type="date" required value={del.decidedOn} onChange={(e) => setDel((f) => ({ ...f, decidedOn: e.target.value }))} />
            </div>
          </div>
          <div className="field">
            <label htmlFor="d-subject">Objet</label>
            <input id="d-subject" required maxLength={500} value={del.subject} onChange={(e) => setDel((f) => ({ ...f, subject: e.target.value }))} />
          </div>
          <ErrorNote error={createDeliberation.error} />
          <div className="actions">
            <button type="submit" className="btn btn-primary" disabled={createDeliberation.isPending}>
              Enregistrer
            </button>
            <button type="button" className="btn" onClick={() => delDialog.current?.close()}>
              Annuler
            </button>
          </div>
        </form>
      </dialog>
    </>
  );
}
